import React, { useEffect, useState } from "react";
import { useAuthContext } from "hooks/useAuthContext";
import PropTypes from "prop-types";
import { useLogout } from "hooks/useLogout";

// MUI Components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import { Avatar, Divider, Popover, List, ListItem, Badge } from "@mui/material";
import {
  AccountCircle,
  ExitToApp,
  MenuOpen,
  MenuSharp,
  Notifications,
} from "@mui/icons-material";

// Custom Components & Styles
import MUSTlogo from "../../../assets/images/logos/must.png";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  navbar,
  navbarContainer,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  setMiniSidenav,
  setTransparentNavbar,
  useMaterialUIController,
} from "context";
import { API_URL } from "config";
import axios from "axios";
import io from "socket.io-client";
import { SOCKET_URL } from "config";
import { toast } from "sonner";
import ReloadAlert from "./component/ReloadAlert";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const { user } = useAuthContext();
  const { logout } = useLogout();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showReloadAlert, setShowReloadAlert] = useState(false);

  // set socket.io
  const socket = io(SOCKET_URL);
  useEffect(() => {
    socket.on("pushNotification", async (data) => {
      const { recipient, message, projectId, recipientRole } = data;
      if (user.email === recipient) {
        if (recipientRole === "student") {
          // Fetch the updated project data
          const response = await fetch(
            `${API_URL}/projects/getprojectby/${projectId}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${user.token}`,
              },
            }
          );

          if (response.ok) {
            const updatedProject = await response.json();

            // Update the project in the localStorage user object
            const storedUser = JSON.parse(localStorage.getItem("user"));
            console.log(storedUser);

            if (storedUser && storedUser.project.id === projectId) {
              storedUser.project = updatedProject; // Update the project
              // Set the updated user back into localStorage
              localStorage.setItem("user", JSON.stringify(storedUser));

              // Reload the page to reflect changes
              setShowReloadAlert(true);
            }

            // Notify the user about the new message
            toast.success("New Message", { description: message });
          } else {
            toast.error("Failed to fetch the project details.try again later");
          }
        } else {
          setShowReloadAlert(true);
          // Notify the user about the new message
          toast.success("New Message", { description: message });
        }
      }
    });

    return () => {
      socket.off("pushNotification");
      socket.disconnect();
    };
  }, [socket, user]);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await fetch(
        `${API_URL}/notification/getnotifications/${user.email}/${user.role}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (!response.ok) {
        setNotifications([]);
      } else {
        const data = await response.json();
        setNotifications(data);
        updateUnreadCount(data);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user]);

  // Update unread count
  const updateUnreadCount = (notifications) => {
    const count = notifications.filter(
      (notif) => notif.status === "unread"
    ).length;
    setUnreadCount(count);
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/notification/${notificationId}/read`);

      setNotifications((prevNotifications) => {
        return prevNotifications.map((notif) =>
          notif.id === notificationId ? { ...notif, status: "read" } : notif
        );
      });

      // Decrement unread count
      setUnreadCount((prevCount) => (prevCount > 0 ? prevCount - 1 : 0));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  useEffect(() => {
    setNavbarType(fixedNavbar ? "sticky" : "static");

    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  // Profile Popover
  const [anchorElProfile, setAnchorElProfile] = useState(null);
  const openProfilePopover = Boolean(anchorElProfile);
  const handleProfileIconClick = (event) =>
    setAnchorElProfile(event.currentTarget);
  const handleCloseProfilePopover = () => setAnchorElProfile(null);

  // Notifications Popover
  const [anchorElNotifications, setAnchorElNotifications] = useState(null);
  const openNotificationsPopover = Boolean(anchorElNotifications);
  const handleNotificationClick = (event) =>
    setAnchorElNotifications(event.currentTarget);
  const handleCloseNotificationsPopover = () => setAnchorElNotifications(null);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <>
      {showReloadAlert && <ReloadAlert onReload={handleReload} />}
      <AppBar
        position={absolute ? "absolute" : navbarType}
        color="inherit"
        sx={(theme) => ({
          ...navbar(theme, { transparentNavbar, absolute, light, darkMode }),
          overflowX: "hidden",
        })}
      >
        <Toolbar sx={(theme) => navbarContainer(theme)}>
          {/* Left Side - Logo & Title */}
          <MDBox sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <MDBox
              component="img"
              src={MUSTlogo}
              alt="MUST Logo"
              width={{ xs: "30%", md: "3%" }}
            />
            <MDTypography ml={2} fontWeight="bold" variant="h6">
              FACULTY OF APPLIED SCIENCES AND TECHNOLOGY - (FAST)
            </MDTypography>
          </MDBox>

          {/* Right Side - Profile & Actions */}
          <MDBox sx={{ display: "flex", alignItems: "center" }}>
            {/* Notifications Icon */}
            <IconButton
              size="small"
              sx={navbarIconButton}
              onClick={handleNotificationClick}
            >
              <Badge badgeContent={unreadCount} color="error">
                <Notifications sx={{ fontSize: 28, color: "#555" }} />
              </Badge>
            </IconButton>
            {/* Profile Icon */}
            <IconButton
              size="small"
              sx={navbarIconButton}
              onClick={handleProfileIconClick}
            >
              <AccountCircle sx={{ fontSize: 32, color: "#555" }} />
            </IconButton>

            {/* Profile Popover */}
            <Popover
              open={openProfilePopover}
              anchorEl={anchorElProfile}
              onClose={handleCloseProfilePopover}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                style: { backgroundColor: "#fff", padding: "10px" },
              }}
            >
              <MDBox p={2} textAlign="center">
                <MDTypography variant="h6">Profile</MDTypography>
                <Avatar sx={{ width: 50, height: 50, my: 1 }} />
                <MDTypography variant="h6">
                  {user.first_name} {user.last_name}
                </MDTypography>
                <Divider sx={{ my: 1 }} />
                <MDTypography variant="body2" color="text.secondary">
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </MDTypography>
                <MDButton
                  color="primary"
                  startIcon={<ExitToApp />}
                  onClick={logout}
                >
                  Logout
                </MDButton>
              </MDBox>
            </Popover>

            {/* Notifications Popover */}
            <Popover
              open={openNotificationsPopover}
              anchorEl={anchorElNotifications}
              onClose={handleCloseNotificationsPopover}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                style: {
                  width: "100%", // default to 100% on small screens
                  maxHeight: "60vh",
                  backgroundColor: "#fff",
                  padding: "10px",
                },
              }}
              sx={{
                "@media (min-width: 1000px)": {
                  width: "50%", // Set 20% width for screens larger than 600px
                  maxWidth: "30%", // maximum width on larger screens
                },
                "@media (max-width: 1000px)": {
                  width: "100%", // Set 100% width for small screens
                  maxWidth: "100%", // maximum width on larger screens
                },
              }}
            >
              <MDBox>
                <MDTypography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 1, fontSize: "1rem" }}
                >
                  Notifications
                </MDTypography>
                <Divider sx={{ my: 1 }} />
                <List sx={{ maxWidth: "100%" }}>
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <ListItem
                        key={notification.id}
                        onClick={() => markAsRead(notification.id)}
                        sx={{
                          cursor: "pointer",
                          borderRadius: 1,
                          backgroundColor:
                            notification.status === "unread"
                              ? "#e3f2fd"
                              : "transparent", // Light blue for unread
                          "&:hover": {
                            backgroundColor:
                              notification.status === "unread"
                                ? "#bbdefb"
                                : "#f5f5f5",
                          },
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          p: 1.5,
                          transition: "background-color 0.3s ease-in-out",
                        }}
                      >
                        {/* Notification Message with Wrapping */}
                        <MDTypography
                          variant="body2"
                          sx={{
                            fontSize: "0.875rem",
                            color: "text.primary",
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                            maxWidth: "100%",
                          }}
                        >
                          {notification.message}
                        </MDTypography>
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <MDTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.875rem" }}
                      >
                        No notifications
                      </MDTypography>
                    </ListItem>
                  )}
                </List>
              </MDBox>
            </Popover>

            {/* Sidenav Toggle */}
            <IconButton
              size="small"
              sx={navbarMobileMenu}
              onClick={handleMiniSidenav}
            >
              {miniSidenav ? (
                <MenuOpen sx={{ fontSize: 30, color: "#555" }} />
              ) : (
                <MenuSharp sx={{ fontSize: 30, color: "#555" }} />
              )}
            </IconButton>
          </MDBox>
        </Toolbar>
      </AppBar>
    </>
  );
}

// Default Props
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// PropTypes
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
